import { FormEvent, useEffect, useState } from 'react';
import { listAccessibleChildren } from '../lib/quizRepository';
import type { QuizChild } from '../lib/quizRepository';
import {
  finishWordPractice,
  loadWordPracticeQuestions,
  loadWordWorldProgress,
  startWordPractice,
  submitWordPracticeAnswer,
} from '../lib/wordWorldRepository';
import type {
  WordAnswerFeedback,
  WordPracticeMode,
  WordPracticeQuestion,
  WordPracticeResult,
  WordWorldProgress,
} from '../lib/wordWorldRepository';
import { useAuth } from '../state/AuthContext';

const modes: Array<{ id: WordPracticeMode; title: string; description: string; example: string }> = [
  { id: 'mixed', title: 'Piko Mix', description: 'Alternate meanings and spelling.', example: 'Best starter practice' },
  { id: 'meaning_match', title: 'Meaning Match', description: 'Choose the child-friendly meaning.', example: 'brave → ready to face difficulty' },
  { id: 'spelling', title: 'Spelling Builder', description: 'Spell a word from its meaning and hint.', example: 'Starts with B · 5 letters' },
];

const emptyProgress: WordWorldProgress = {
  totalAttempts: 0,
  correctAttempts: 0,
  accuracyPercent: 0,
  learningWords: 0,
  masteredWords: 0,
  availableWords: 0,
  lastPractisedAt: null,
};

function WordResultCard({ result, onRestart }: { result: WordPracticeResult; onRestart(): void }) {
  const minutes = Math.floor(result.durationSeconds / 60);
  const seconds = result.durationSeconds % 60;
  return (
    <section className="math-result word-result panel">
      <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
      <p className="eyebrow">Word practice complete</p>
      <h2>{result.percentage >= 90 ? 'Word champion!' : result.percentage >= 70 ? 'Wonderful word work!' : 'Keep growing your word power!'}</h2>
      <strong>{result.score} / {result.questionCount}</strong>
      <p>{result.percentage}% correct · {minutes}:{String(seconds).padStart(2, '0')}</p>
      <span className="pill green">Result and mastery saved securely</span>
      <button className="button" type="button" onClick={onRestart}>Practise again</button>
    </section>
  );
}

export function WordWorldPage() {
  const { user, isSupabaseConfigured } = useAuth();
  const [children, setChildren] = useState<QuizChild[]>([]);
  const [childId, setChildId] = useState('');
  const [progress, setProgress] = useState<WordWorldProgress>(emptyProgress);
  const [practiceMode, setPracticeMode] = useState<WordPracticeMode>('mixed');
  const [questionCount, setQuestionCount] = useState(4);
  const [sessionId, setSessionId] = useState('');
  const [questions, setQuestions] = useState<WordPracticeQuestion[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [feedback, setFeedback] = useState<WordAnswerFeedback | null>(null);
  const [questionStartedAt, setQuestionStartedAt] = useState(Date.now());
  const [result, setResult] = useState<WordPracticeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const selectedChild = children.find((child) => child.id === childId);
  const currentQuestion = questions[questionIndex];
  const effectiveQuestionCount = Math.min(questionCount, progress.availableWords || questionCount);

  useEffect(() => {
    if (!isSupabaseConfigured || user?.demo) return;
    let active = true;
    setBusy(true);
    void listAccessibleChildren()
      .then((items) => {
        if (!active) return;
        setChildren(items);
        setChildId((current) => current || items[0]?.id || '');
      })
      .catch((error: unknown) => active && setMessage(error instanceof Error ? error.message : 'Unable to load learners.'))
      .finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [isSupabaseConfigured, user?.demo]);

  useEffect(() => {
    if (!childId || !isSupabaseConfigured || user?.demo) return;
    let active = true;
    void loadWordWorldProgress(childId)
      .then((item) => active && setProgress(item))
      .catch((error: unknown) => active && setMessage(error instanceof Error ? error.message : 'Unable to load Word World progress.'));
    return () => { active = false; };
  }, [childId, isSupabaseConfigured, user?.demo]);

  async function beginPractice() {
    if (!childId || effectiveQuestionCount < 1) return;
    setBusy(true);
    setMessage('');
    try {
      const newSessionId = await startWordPractice(childId, practiceMode, effectiveQuestionCount);
      const sessionQuestions = await loadWordPracticeQuestions(newSessionId);
      if (!sessionQuestions.length) throw new Error('No reviewed words were available for this learner.');
      setSessionId(newSessionId);
      setQuestions(sessionQuestions);
      setQuestionIndex(0);
      setTypedAnswer('');
      setSelectedOptionId('');
      setFeedback(null);
      setQuestionStartedAt(Date.now());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to start Word World.');
    } finally {
      setBusy(false);
    }
  }

  async function checkAnswer(event: FormEvent) {
    event.preventDefault();
    if (!currentQuestion) return;
    const hasAnswer = currentQuestion.practiceMode === 'meaning_match' ? Boolean(selectedOptionId) : Boolean(typedAnswer.trim());
    if (!hasAnswer) return;
    setBusy(true);
    setMessage('');
    try {
      setFeedback(await submitWordPracticeAnswer(
        sessionId,
        currentQuestion.questionNumber,
        currentQuestion.practiceMode === 'spelling' ? typedAnswer : null,
        currentQuestion.practiceMode === 'meaning_match' ? selectedOptionId : null,
        Date.now() - questionStartedAt,
      ));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to check the word answer.');
    } finally {
      setBusy(false);
    }
  }

  async function nextQuestion() {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex((current) => current + 1);
      setTypedAnswer('');
      setSelectedOptionId('');
      setFeedback(null);
      setMessage('');
      setQuestionStartedAt(Date.now());
      return;
    }
    setBusy(true);
    try {
      const finished = await finishWordPractice(sessionId);
      setResult(finished);
      setProgress(await loadWordWorldProgress(childId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to finish Word World practice.');
    } finally {
      setBusy(false);
    }
  }

  function resetPractice() {
    setSessionId('');
    setQuestions([]);
    setQuestionIndex(0);
    setTypedAnswer('');
    setSelectedOptionId('');
    setFeedback(null);
    setResult(null);
    setMessage('');
  }

  if (!isSupabaseConfigured || user?.demo) {
    return (
      <div className="portal-page">
        <section className="empty-state">
          <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
          <h3>Word World requires a real staging login</h3>
          <p>Demo roles cannot save word practice or learner mastery.</p>
        </section>
      </div>
    );
  }

  if (result) return <div className="portal-page"><WordResultCard result={result} onRestart={resetPractice} /></div>;

  if (sessionId && currentQuestion) {
    const hasAnswer = currentQuestion.practiceMode === 'meaning_match' ? Boolean(selectedOptionId) : Boolean(typedAnswer.trim());
    return (
      <div className="portal-page word-world-page">
        <header className="page-header">
          <div>
            <p className="eyebrow">English Word World</p>
            <h1>{selectedChild?.displayName}’s word practice</h1>
            <p>Answers and word mastery are verified and saved by Supabase staging.</p>
          </div>
          <span className="pill green">No answer key in browser</span>
        </header>
        {message && <div className="form-message error quiz-message">{message}</div>}
        {busy && <div className="quiz-busy">Piko is checking the word…</div>}
        <section className="word-practice-stage">
          <div className="quiz-progress-row">
            <span>Question {questionIndex + 1} of {questions.length}</span>
            <progress value={questionIndex + (feedback ? 1 : 0)} max={questions.length} />
            <span>{feedback?.currentScore ?? questionIndex} correct</span>
          </div>
          <form className="word-question-card" onSubmit={(event) => void checkAnswer(event)}>
            <span className="pill purple">{currentQuestion.practiceMode === 'meaning_match' ? 'Meaning Match' : 'Spelling Builder'}</span>
            {currentQuestion.practiceMode === 'meaning_match' ? (
              <>
                <p className="word-instruction">Choose the meaning of</p>
                <h2 className="word-prompt">{currentQuestion.prompt}</h2>
                <span className="word-hint">{currentQuestion.hint}</span>
                <div className="word-options">
                  {currentQuestion.options.map((option, optionIndex) => (
                    <button
                      key={option.id}
                      type="button"
                      disabled={Boolean(feedback)}
                      className={selectedOptionId === option.id ? 'selected' : ''}
                      onClick={() => setSelectedOptionId(option.id)}
                    >
                      <span>{String.fromCharCode(65 + optionIndex)}</span>{option.text}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="word-instruction">Spell the word that means</p>
                <h2 className="spelling-prompt">{currentQuestion.prompt}</h2>
                <p className="word-example">{currentQuestion.example}</p>
                <span className="word-hint">{currentQuestion.hint}</span>
                <label className="word-answer-label">
                  Your spelling
                  <input
                    value={typedAnswer}
                    disabled={Boolean(feedback)}
                    onChange={(event) => setTypedAnswer(event.target.value.replace(/[^A-Za-z '-]/g, '').slice(0, 80))}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    autoFocus
                  />
                </label>
              </>
            )}
            {feedback && (
              <div className={`answer-feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                <strong>{feedback.feedbackMessage}</strong>
                {!feedback.isCorrect && <span>Correct answer: {feedback.correctAnswer}</span>}
              </div>
            )}
            <div className="quiz-footer-actions">
              {!feedback
                ? <button className="button" type="submit" disabled={busy || !hasAnswer}>Check answer</button>
                : <button className="button" type="button" onClick={() => void nextQuestion()}>{questionIndex === questions.length - 1 ? 'Finish practice' : 'Next word'}</button>}
            </div>
          </form>
        </section>
      </div>
    );
  }

  return (
    <div className="portal-page word-world-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">English Word World</p>
          <h1>Grow spelling and vocabulary with Piko</h1>
          <p>Level-matched meanings and spelling practice with secure learner mastery.</p>
        </div>
        <span className="pill green">32 starter words · drafts protected</span>
      </header>

      {message && <div className="form-message error quiz-message">{message}</div>}
      {busy && <div className="quiz-busy">Piko is preparing Word World…</div>}

      <section className="math-summary-grid word-summary-grid">
        <article><span>Total attempts</span><strong>{progress.totalAttempts}</strong></article>
        <article><span>Accuracy</span><strong>{progress.accuracyPercent}%</strong></article>
        <article><span>Learning words</span><strong>{progress.learningWords}</strong></article>
        <article><span>Mastered words</span><strong>{progress.masteredWords}/{progress.availableWords}</strong></article>
      </section>

      <section className="word-setup-grid">
        <aside className="panel word-controls">
          <label>
            Learner
            <select value={childId} onChange={(event) => setChildId(event.target.value)}>
              {children.map((child) => <option key={child.id} value={child.id}>{child.displayName} · {child.levelCode ?? 'Level not set'}</option>)}
            </select>
          </label>
          {!children.length && <p>No accessible learner profile was found.</p>}

          <fieldset>
            <legend>Practice mode</legend>
            <div className="word-mode-grid">
              {modes.map((mode) => (
                <button key={mode.id} className={practiceMode === mode.id ? 'selected' : ''} type="button" onClick={() => setPracticeMode(mode.id)}>
                  <strong>{mode.title}</strong>
                  <span>{mode.description}</span>
                  <small>{mode.example}</small>
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>Number of words</legend>
            <div className="math-count-buttons">
              {[4, 6, 8].map((count) => <button key={count} className={questionCount === count ? 'selected' : ''} type="button" onClick={() => setQuestionCount(count)}>{count}</button>)}
            </div>
          </fieldset>

          <button className="button math-start" type="button" disabled={!childId || progress.availableWords < 1 || busy} onClick={() => void beginPractice()}>
            Start {effectiveQuestionCount}-word practice
          </button>
        </aside>

        <section className="panel word-world-intro">
          <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
          <div>
            <p className="eyebrow">Starter learning loop</p>
            <h2>See it. Understand it. Spell it.</h2>
            <p>Piko mixes simple meanings, example sentences and spelling hints. Every response updates only the selected learner’s protected mastery record.</p>
            <div className="word-feature-list">
              <span><strong>1</strong> Level-matched words</span>
              <span><strong>2</strong> Server-checked answers</span>
              <span><strong>3</strong> Review before publication</span>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}

